import { MaterialPrepError, OCR_MAX_EDGE_PX } from '../extraction/extractionTypes';
import { PhotoRotation } from './photoTypes';

/**
 * What `preparePhotoImage` needs to decode and redraw a photo. `defaultDeps` reaches the
 * browser globals lazily, so importing this module in a node test touches neither.
 */
export interface PreparePhotoDeps {
  createImageBitmap: (image: Blob, options: ImageBitmapOptions) => Promise<ImageBitmap>;
  createCanvas: (width: number, height: number) => OffscreenCanvas;
}

const defaultDeps: PreparePhotoDeps = {
  createImageBitmap: (image, options) => globalThis.createImageBitmap(image, options),
  createCanvas: (width, height) => new OffscreenCanvas(width, height),
};

/**
 * Turns one admin-added photo into the image OCR reads (PHO-D3): decoded upright by its EXIF
 * orientation, turned by the admin's `rotation` on top of that, and scaled down — never up —
 * so its longer edge is at most `OCR_MAX_EDGE_PX`. The result is a PNG blob.
 */
export const preparePhotoImage = async (
  file: Blob,
  rotation: PhotoRotation,
  deps: PreparePhotoDeps = defaultDeps
): Promise<Blob> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await deps.createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new MaterialPrepError('PhotoUnreadable');
  }

  let canvas: OffscreenCanvas | undefined;
  try {
    const { width: w, height: h } = bitmap;
    const scale = Math.min(1, OCR_MAX_EDGE_PX / Math.max(w, h));
    const scaledW = Math.max(1, Math.min(OCR_MAX_EDGE_PX, Math.round(w * scale)));
    const scaledH = Math.max(1, Math.min(OCR_MAX_EDGE_PX, Math.round(h * scale)));
    const swapped = rotation === 90 || rotation === 270;
    const outW = swapped ? scaledH : scaledW;
    const outH = swapped ? scaledW : scaledH;

    canvas = deps.createCanvas(outW, outH);
    const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
    // A transparent PNG must not read as black once drawn onto the canvas.
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, outW, outH);
    ctx.translate(outW / 2, outH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(bitmap, (-w * scale) / 2, (-h * scale) / 2, w * scale, h * scale);

    try {
      return await canvas.convertToBlob({ type: 'image/png' });
    } catch {
      throw new MaterialPrepError('unknown');
    }
  } finally {
    // A phone photo is tens of megapixels; release both at once rather than wait for the GC.
    bitmap.close();
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
};
